import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { leaderboardService, tournamentsService } from '../../services/supabaseService';
import Header from '../../components/navigation/Header';
import MetricCard from './components/MetricCard';
import AccuracyTrendChart from './components/AccuracyTrendChart';
import WinRateChart from './components/WinRateChart';
import PerformanceRadarChart from './components/PerformanceRadarChart';
import TournamentBreakdown from './components/TournamentBreakdown';
import FilterBar from './components/FilterBar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AnalyticsDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: '30d',
    tournamentType: 'all',
    predictionCategory: 'all'
  });

  // Analytics data state
  const [metrics, setMetrics] = useState({
    overallAccuracy: 0,
    totalTournaments: 0,
    currentStreak: 0,
    totalEarnings: 0,
    accuracyTrend: 0,
    tournamentsTrend: 0,
    streakTrend: 0,
    earningsTrend: 0
  });

  const [accuracyData, setAccuracyData] = useState([]);
  const [winRateData, setWinRateData] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [tournamentBreakdownData, setTournamentBreakdownData] = useState([]);

  useEffect(() => {
    if (userProfile) {
      fetchAnalyticsData();
    }
  }, [userProfile, filters]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user's leaderboard data across all tournaments
      const leaderboardEntries = await leaderboardService?.getUserLeaderboardEntries(userProfile?.id);
      
      // Fetch all tournaments user participated in
      const tournaments = await tournamentsService?.getAll();
      const userTournaments = tournaments?.filter(t => t?.isParticipating) || [];

      // Calculate metrics
      const totalAccuracy = leaderboardEntries?.reduce((sum, entry) => sum + (entry?.accuracy || 0), 0) || 0;
      const avgAccuracy = leaderboardEntries?.length > 0 ? (totalAccuracy / leaderboardEntries?.length) : 0;
      
      const totalPoints = leaderboardEntries?.reduce((sum, entry) => sum + (entry?.totalPoints || 0), 0) || 0;
      const totalPredictions = leaderboardEntries?.reduce((sum, entry) => sum + (entry?.totalPredictions || 0), 0) || 0;
      const correctPredictions = leaderboardEntries?.reduce((sum, entry) => sum + (entry?.correctPredictions || 0), 0) || 0;

      // Calculate streak (simplified - consecutive correct predictions)
      let streak = calculateStreak(leaderboardEntries);

      // Calculate trends (mock data - in real app, compare with previous period)
      const accuracyTrendValue = 5.2;
      const tournamentsTrend = 12;
      const streakTrend = 3;
      const earningsTrend = 8.5;

      setMetrics({
        overallAccuracy: avgAccuracy?.toFixed(1),
        totalTournaments: userTournaments?.length,
        currentStreak: streak,
        totalEarnings: totalPoints,
        accuracyTrend: accuracyTrendValue,
        tournamentsTrend,
        streakTrend,
        earningsTrend
      });

      // Generate accuracy trend data (last 30 days)
      const accuracyTrend = generateAccuracyTrendData(leaderboardEntries);
      setAccuracyData(accuracyTrend);

      // Generate win rate by tournament type
      const winRates = generateWinRateData(userTournaments, leaderboardEntries);
      setWinRateData(winRates);

      // Generate performance comparison data
      const performance = generatePerformanceData(leaderboardEntries);
      setPerformanceData(performance);

      // Generate tournament breakdown
      const breakdown = generateTournamentBreakdown(userTournaments, leaderboardEntries);
      setTournamentBreakdownData(breakdown);

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (entries) => {
    if (!entries || entries?.length === 0) return 0;
    
    // Sort by most recent
    const sorted = [...entries]?.sort((a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt));
    
    let streak = 0;
    for (const entry of sorted) {
      if (entry?.accuracy >= 50) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const generateAccuracyTrendData = (entries) => {
    // Generate last 30 days of data
    const days = 30;
    const data = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date?.setDate(date?.getDate() - i);
      
      // Mock accuracy data with some variation
      const baseAccuracy = 65 + Math.random() * 20;
      
      data?.push({
        date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        accuracy: parseFloat(baseAccuracy?.toFixed(1)),
        predictions: Math.floor(Math.random() * 10) + 5
      });
    }

    return data;
  };

  const generateWinRateData = (tournaments, entries) => {
    const formatCounts = {
      'T20': { total: 0, correct: 0 },
      'ODI': { total: 0, correct: 0 },
      'Test': { total: 0, correct: 0 }
    };

    tournaments?.forEach(tournament => {
      const format = tournament?.format || 'T20';
      const entry = entries?.find(e => e?.tournamentId === tournament?.id);
      
      if (entry) {
        formatCounts[format].total += entry?.totalPredictions || 0;
        formatCounts[format].correct += entry?.correctPredictions || 0;
      }
    });

    return Object?.keys(formatCounts)?.map(format => ({
      format,
      winRate: formatCounts?.[format]?.total > 0 
        ? ((formatCounts?.[format]?.correct / formatCounts?.[format]?.total) * 100)?.toFixed(1)
        : 0,
      total: formatCounts?.[format]?.total
    }));
  };

  const generatePerformanceData = (entries) => {
    if (!entries || entries?.length === 0) {
      return [
        { category: 'Match Winner', userScore: 0, platformAvg: 0 },
        { category: 'Top Scorer', userScore: 0, platformAvg: 0 },
        { category: 'Top Wicket Taker', userScore: 0, platformAvg: 0 },
        { category: 'Man of Match', userScore: 0, platformAvg: 0 },
        { category: 'Overall', userScore: 0, platformAvg: 0 }
      ];
    }

    const avgAccuracy = entries?.reduce((sum, e) => sum + (e?.accuracy || 0), 0) / entries?.length;

    return [
      { category: 'Match Winner', userScore: avgAccuracy + 5, platformAvg: 65 },
      { category: 'Top Scorer', userScore: avgAccuracy - 3, platformAvg: 58 },
      { category: 'Top Wicket Taker', userScore: avgAccuracy + 2, platformAvg: 60 },
      { category: 'Man of Match', userScore: avgAccuracy - 5, platformAvg: 55 },
      { category: 'Overall', userScore: avgAccuracy, platformAvg: 62 }
    ];
  };

  const generateTournamentBreakdown = (tournaments, entries) => {
    return tournaments?.map(tournament => {
      const entry = entries?.find(e => e?.tournamentId === tournament?.id);
      
      return {
        id: tournament?.id,
        name: tournament?.name,
        format: tournament?.format,
        status: tournament?.status,
        accuracy: entry?.accuracy || 0,
        totalPredictions: entry?.totalPredictions || 0,
        correctPredictions: entry?.correctPredictions || 0,
        pointsEarned: entry?.totalPoints || 0,
        rank: entry?.rank || '-',
        matches: [] // Would be populated with match-by-match data
      };
    }) || [];
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExport = () => {
    // Export analytics data as CSV
    const csvData = generateCSVReport();
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window?.URL?.revokeObjectURL(url);
  };

  const generateCSVReport = () => {
    let csv = 'Analytics Report\n\n';
    csv += 'Overall Metrics\n';
    csv += `Accuracy,${metrics?.overallAccuracy}%\n`;
    csv += `Tournaments,${metrics?.totalTournaments}\n`;
    csv += `Win Streak,${metrics?.currentStreak}\n`;
    csv += `Total Points,${metrics?.totalEarnings}\n\n`;
    
    csv += 'Tournament Breakdown\n';
    csv += 'Name,Format,Accuracy,Predictions,Points,Rank\n';
    tournamentBreakdownData?.forEach(t => {
      csv += `${t?.name},${t?.format},${t?.accuracy}%,${t?.totalPredictions},${t?.pointsEarned},${t?.rank}\n`;
    });
    
    return csv;
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Analytics Dashboard | PowerPlay</title>
        </Helmet>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading analytics...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analytics Dashboard | PowerPlay</title>
        <meta name="description" content="Comprehensive analytics showing prediction accuracy, win rates, tournament performance trends, and earning patterns" />
      </Helmet>
      <Header />
      
      <div className="min-h-screen bg-background pt-20 md:pt-24">
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Analytics Dashboard
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Comprehensive insights into your prediction performance
                </p>
              </div>
              <Button
                variant="outline"
                iconName="Download"
                onClick={handleExport}
                className="w-full md:w-auto"
              >
                Export Report
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <FilterBar filters={filters} onFilterChange={handleFilterChange} />

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error">{error}</p>
            </div>
          )}

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <MetricCard
              title="Overall Accuracy"
              value={`${metrics?.overallAccuracy}%`}
              icon="Target"
              trend={metrics?.accuracyTrend >= 0 ? 'up' : 'down'}
              trendValue={`${Math.abs(metrics?.accuracyTrend)}%`}
              color="primary"
            />
            <MetricCard
              title="Tournaments Participated"
              value={metrics?.totalTournaments}
              icon="Trophy"
              trend={metrics?.tournamentsTrend >= 0 ? 'up' : 'down'}
              trendValue={`${Math.abs(metrics?.tournamentsTrend)}%`}
              color="success"
            />
            <MetricCard
              title="Current Win Streak"
              value={metrics?.currentStreak}
              icon="TrendingUp"
              trend={metrics?.streakTrend >= 0 ? 'up' : 'down'}
              trendValue={`${Math.abs(metrics?.streakTrend)}`}
              color="warning"
            />
            <MetricCard
              title="Total Points Earned"
              value={metrics?.totalEarnings?.toLocaleString()}
              icon="Award"
              trend={metrics?.earningsTrend >= 0 ? 'up' : 'down'}
              trendValue={`${Math.abs(metrics?.earningsTrend)}%`}
              color="success"
            />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 md:mb-8">
            {/* Accuracy Trend Chart */}
            <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 border border-border">
              <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Prediction Accuracy Trend
              </h2>
              <AccuracyTrendChart data={accuracyData} />
            </div>

            {/* Win Rate by Tournament Type */}
            <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 border border-border">
              <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Win Rate by Tournament Type
              </h2>
              <WinRateChart data={winRateData} />
            </div>
          </div>

          {/* Performance Comparison */}
          <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 border border-border mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
              Performance vs Platform Average
            </h2>
            <PerformanceRadarChart data={performanceData} />
          </div>

          {/* Tournament Breakdown */}
          <div className="bg-card rounded-lg shadow-elevation-sm p-4 md:p-6 border border-border">
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
              Tournament Performance Breakdown
            </h2>
            <TournamentBreakdown data={tournamentBreakdownData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalyticsDashboard;